/**
 * GST Validation Utilities
 * Handles GSTIN validation, invoice data validation, and filing period checks
 */

/**
 * Validate GSTIN format
 * Format: 2 digits (state code) + 10 alphanumeric + Z + 1 alphanumeric
 * Example: 27ABCDE5678G1Z3
 * 
 * @param {string} gstin - GSTIN to validate
 * @returns {Object} - { valid: boolean, error: string }
 */
export const validateGSTIN = (gstin) => {
  if (!gstin) {
    return { valid: false, error: 'GSTIN is required' };
  }

  // Remove spaces and convert to uppercase
  const cleanGSTIN = gstin.trim().toUpperCase();

  // Check length
  if (cleanGSTIN.length !== 15) {
    return { valid: false, error: 'GSTIN must be 15 characters long' };
  }

  // GSTIN format regex: 2 digits + 10 alphanumeric + Z + 1 alphanumeric
  const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
  
  if (!gstinRegex.test(cleanGSTIN)) {
    return { valid: false, error: 'Invalid GSTIN format' };
  }

  // Validate state code (01-37 are valid Indian state codes)
  const stateCode = parseInt(cleanGSTIN.substring(0, 2), 10);
  if (stateCode < 1 || stateCode > 38) {
    return { valid: false, error: 'Invalid state code in GSTIN' };
  }

  return { valid: true, error: null, gstin: cleanGSTIN };
};

/**
 * Extract state code from GSTIN
 * @param {string} gstin - GSTIN
 * @returns {string} - State code (2 digits)
 */
export const getStateCodeFromGSTIN = (gstin) => {
  if (!gstin || gstin.length < 2) return null;
  return gstin.substring(0, 2);
};

/**
 * Check if transaction is interstate
 * @param {string} sellerGSTIN - Seller's GSTIN
 * @param {string} buyerGSTIN - Buyer's GSTIN
 * @returns {boolean} - true if interstate, false if intrastate
 */
export const isInterstate = (sellerGSTIN, buyerGSTIN) => {
  if (!sellerGSTIN || !buyerGSTIN) return false;
  const sellerState = getStateCodeFromGSTIN(sellerGSTIN);
  const buyerState = getStateCodeFromGSTIN(buyerGSTIN);
  return sellerState !== buyerState;
};

/**
 * Validate invoice data for GST filing
 * @param {Object} invoice - Invoice object
 * @returns {Object} - { valid: boolean, errors: string[] }
 */
export const validateInvoiceForGST = (invoice) => {
  const errors = [];

  // Check required fields
  if (!invoice.invoiceNo) {
    errors.push('Invoice number is required');
  }

  if (!invoice.createdDate) {
    errors.push('Invoice date is required');
  }

  if (!invoice.totalAmount || parseFloat(invoice.totalAmount) <= 0) {
    errors.push('Invoice amount must be greater than 0');
  }

  // Validate company GSTIN
  const companyGSTIN = invoice.companyDetail?.companyGST;
  if (companyGSTIN) {
    const gstValidation = validateGSTIN(companyGSTIN);
    if (!gstValidation.valid) {
      errors.push(`Company GSTIN invalid: ${gstValidation.error}`);
    }
  } else {
    errors.push('Company GSTIN is required');
  }

  // Validate client GSTIN if present (B2B transaction)
  const clientGSTIN = invoice.clientDetail?.GST;
  if (clientGSTIN && clientGSTIN.trim()) {
    const gstValidation = validateGSTIN(clientGSTIN);
    if (!gstValidation.valid) {
      errors.push(`Client GSTIN invalid: ${gstValidation.error}`);
    }
  }

  // Validate taxes
  if (!invoice.taxes || invoice.taxes.length === 0) {
    errors.push('Invoice must have tax information');
  } else {
    // Check if tax amounts are valid
    const hasValidTax = invoice.taxes.some(tax => {
      const amount = parseFloat(tax.amount);
      return amount > 0;
    });
    if (!hasValidTax) {
      errors.push('Invoice must have valid tax amounts');
    }
  }

  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * Validate filing period format
 * @param {string} period - Period in MMYYYY format
 * @returns {Object} - { valid: boolean, error: string }
 */
export const validateFilingPeriod = (period) => {
  if (!period) {
    return { valid: false, error: 'Filing period is required' };
  }

  // Check format MMYYYY
  const periodRegex = /^(0[1-9]|1[0-2])\d{4}$/;
  if (!periodRegex.test(period)) {
    return { valid: false, error: 'Filing period must be in MMYYYY format' };
  }

  const month = parseInt(period.substring(0, 2), 10);
  const year = parseInt(period.substring(2, 6), 10);

  // Check if period is in the future
  const now = new Date();
  const periodDate = new Date(year, month - 1, 1);
  const currentPeriodDate = new Date(now.getFullYear(), now.getMonth(), 1);

  if (periodDate > currentPeriodDate) {
    return { valid: false, error: 'Cannot file GST for future periods' };
  }

  return { valid: true, error: null, month, year };
};

/**
 * Get GSTR-1 due date for a given period
 * Due by 11th of next month
 * @param {string} period - Period in MMYYYY format
 * @returns {Date} - Due date
 */
export const getGSTR1DueDate = (period) => {
  const month = parseInt(period.substring(0, 2), 10);
  const year = parseInt(period.substring(2, 6), 10);
  
  // Due date is 11th of next month
  const dueMonth = month === 12 ? 1 : month + 1;
  const dueYear = month === 12 ? year + 1 : year;
  
  return new Date(dueYear, dueMonth - 1, 11, 23, 59, 59);
};

/**
 * Get GSTR-3B due date for a given period
 * Due by 20th of next month
 * @param {string} period - Period in MMYYYY format
 * @returns {Date} - Due date
 */
export const getGSTR3BDueDate = (period) => {
  const month = parseInt(period.substring(0, 2), 10);
  const year = parseInt(period.substring(2, 6), 10);
  
  // Due date is 20th of next month
  const dueMonth = month === 12 ? 1 : month + 1;
  const dueYear = month === 12 ? year + 1 : year;
  
  return new Date(dueYear, dueMonth - 1, 20, 23, 59, 59);
};

/**
 * Check if filing is late
 * @param {string} period - Period in MMYYYY format
 * @param {string} returnType - 'GSTR1' or 'GSTR3B'
 * @returns {Object} - { isLate: boolean, dueDate: Date, daysLate: number }
 */
export const checkFilingLateness = (period, returnType = 'GSTR1') => {
  const now = new Date();
  const dueDate = returnType === 'GSTR3B' ? getGSTR3BDueDate(period) : getGSTR1DueDate(period);
  
  const isLate = now > dueDate;
  const daysLate = isLate ? Math.floor((now - dueDate) / (1000 * 60 * 60 * 24)) : 0;
  
  return {
    isLate,
    dueDate,
    daysLate
  };
};

/**
 * Validate tax calculation accuracy
 * @param {number} taxableValue - Taxable value
 * @param {number} taxRate - Tax rate (e.g., 5, 12, 18, 28)
 * @param {Object} taxes - Object with cgst, sgst, igst amounts
 * @returns {Object} - { valid: boolean, error: string, expected: Object }
 */
export const validateTaxCalculation = (taxableValue, taxRate, taxes) => {
  const expectedTax = (taxableValue * taxRate) / 100;
  const actualTax = (taxes.cgst || 0) + (taxes.sgst || 0) + (taxes.igst || 0);
  
  // Allow 1 rupee tolerance for rounding
  const tolerance = 1;
  const difference = Math.abs(expectedTax - actualTax);
  
  if (difference > tolerance) {
    return {
      valid: false,
      error: `Tax calculation mismatch. Expected: ₹${expectedTax.toFixed(2)}, Got: ₹${actualTax.toFixed(2)}`,
      expected: {
        totalTax: expectedTax,
        difference
      }
    };
  }
  
  return {
    valid: true,
    error: null,
    expected: {
      totalTax: expectedTax,
      difference
    }
  };
};

/**
 * Batch validate multiple invoices
 * @param {Array} invoices - Array of invoice objects
 * @returns {Object} - { valid: boolean, validInvoices: Array, invalidInvoices: Array }
 */
export const batchValidateInvoices = (invoices) => {
  const validInvoices = [];
  const invalidInvoices = [];

  invoices.forEach(invoice => {
    const validation = validateInvoiceForGST(invoice);
    if (validation.valid) {
      validInvoices.push(invoice);
    } else {
      invalidInvoices.push({
        invoice,
        errors: validation.errors
      });
    }
  });

  return {
    valid: invalidInvoices.length === 0,
    validInvoices,
    invalidInvoices,
    summary: {
      total: invoices.length,
      valid: validInvoices.length,
      invalid: invalidInvoices.length
    }
  };
};

const gstValidatorUtils = {
  validateGSTIN,
  getStateCodeFromGSTIN,
  isInterstate,
  validateInvoiceForGST,
  validateFilingPeriod,
  getGSTR1DueDate,
  getGSTR3BDueDate,
  checkFilingLateness,
  validateTaxCalculation,
  batchValidateInvoices
};

export default gstValidatorUtils;


